import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { getApiError } from '../redux/api/baseQuery';

const GERMAN_MESSAGES = {
  validation_error: 'Eingaben sind ungültig.',
  not_authenticated: 'Bitte erneut anmelden.',
  authentication_failed: 'Anmeldung fehlgeschlagen.',
  permission_denied: 'Keine Berechtigung für diese Aktion.',
  not_found: 'Nicht gefunden.',
  insufficient_stock: 'Nicht genügend Lagerbestand.',
  contingent_exceeded: 'Kontingent überschritten.',
  delivery_already_confirmed: 'Lieferung wurde bereits bestätigt.',
  session_already_closed: 'Kassensitzung ist bereits geschlossen.',
  cash_register_already_open: 'Kasse ist bereits geöffnet.',
  tse_unavailable: 'TSE momentan nicht erreichbar.',
  todo_already_claimed: 'Diese Aufgabe wurde gerade von jemand anderem übernommen.',
  todo_already_completed: 'Diese Aufgabe ist bereits erledigt.',
  todo_not_claimed_by_you: 'Du kannst nur eigene Aufgaben freigeben.',
  event_read_only:
    'Dieses Event ist abgeschlossen — Änderungen sind nicht mehr möglich.',
  internal_error: 'Ein Serverfehler ist aufgetreten.',
  network_error: 'Verbindung zum Server fehlgeschlagen.',
};

// API field names → German labels, used to prefix field-level detail lines.
// Extend as needed for fields users actually see.
const FIELD_LABELS = {
  customer_name: 'Name',
  customer_email: 'E-Mail',
  customer_phone: 'Telefon',
  note: 'Notiz',
  pickup_until: 'Abholdatum',
  items: 'Artikel',
  article: 'Artikel',
  quantity: 'Menge',
};

// Specific English API messages → German. Matched on the exact (trimmed)
// string the backend returns. Add entries here for messages that need a clear,
// understandable German wording (mostly DRF's built-in validator strings).
const MESSAGE_TRANSLATIONS = {
  'Enter a valid email address.': 'Bitte eine gültige E-Mail-Adresse eingeben.',
  'This field is required.': 'Dieses Feld ist erforderlich.',
  'This field may not be blank.': 'Dieses Feld darf nicht leer sein.',
  'This field may not be null.': 'Dieses Feld darf nicht leer sein.',
  'A valid integer is required.': 'Bitte eine gültige Zahl eingeben.',
  'Date has wrong format. Use one of these formats instead: YYYY-MM-DD.':
    'Ungültiges Datum.',
};

const translateMessage = (msg) =>
  (typeof msg === 'string' && MESSAGE_TRANSLATIONS[msg.trim()]) || msg;

/**
 * Format an ApiError-shaped value into a user-facing German string.
 * Falls back to the backend message, then to a generic line.
 */
export const formatApiError = (err) => {
  const e = getApiError(err);
  if (e.code === 'insufficient_stock' && e.details && e.details._) {
    return e.details._.join('\n');
  }
  // Surface field-level details inline whenever they're present, regardless of
  // the error code (e.g. 'validation_error', 'invalid', ...).
  if (e.details && typeof e.details === 'object') {
    const lines = [];
    for (const [field, msgs] of Object.entries(e.details)) {
      // Only render entries that look like field errors (string or list of
      // strings); skip nested objects to avoid leaking "[object Object]".
      const isStringList =
        Array.isArray(msgs) && msgs.every((m) => typeof m === 'string');
      if (!isStringList && typeof msgs !== 'string') continue;
      const isGeneric = field === '_' || field === 'non_field_errors';
      const label = isGeneric ? '' : `${FIELD_LABELS[field] || field}: `;
      const parts = Array.isArray(msgs) ? msgs : [msgs];
      const text = parts.map(translateMessage).join(' ');
      lines.push(`${label}${text}`);
    }
    if (lines.length > 0) return lines.join('\n');
  }
  return GERMAN_MESSAGES[e.code] || translateMessage(e.message) || 'Unbekannter Fehler';
};

/**
 * Hook returning a stable toaster that maps API errors to German strings.
 * Returns the normalized ApiError so callers can branch on code.
 */
export const useApiErrorToast = () => {
  return useCallback((err, fallback) => {
    const e = getApiError(err);
    const msg = formatApiError(err) || fallback || 'Unbekannter Fehler';
    toast.error(msg);
    return e;
  }, []);
};
